import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import CloseRoundedIcon
  from '@mui/icons-material/CloseRounded';

import {
  getMarginCenterCustomerPositionRanking,
} from '../services/marginCenterService';

import {
  formatMarginCenterCurrency,
  formatMarginCenterNumber,
} from '../utils/numberFormat';

import type {
  MarginCenterComparisonMode,
} from '../types/marginCenter';

import type {
  MarginCenterCustomerPositionGroupDimension,
} from '../types/customerPosition';

import type {
  MarginCenterCustomerPositionRankingMetric,
  MarginCenterCustomerPositionRankingParams,
  MarginCenterCustomerPositionRankingRow,
  MarginCenterCustomerPositionRankingScopeType,
} from '../types/customerPositionRanking';


interface CustomerPositionRankingModalProps {
  open: boolean;

  customerId: string;

  metric:
    MarginCenterCustomerPositionRankingMetric;

  scope:
    MarginCenterCustomerPositionRankingScopeType;

  groupDimension?:
    MarginCenterCustomerPositionGroupDimension;

  startDate?: string | null;
  endDate?: string | null;

  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate?: string | null;
  comparisonEndDate?: string | null;

  currencyCode: string | null;

  onClose: () => void;
}


function metricTitle(
  metric:
    MarginCenterCustomerPositionRankingMetric,
): string {
  if (metric === 'revenue') {
    return 'Clasificación por facturación';
  }

  if (metric === 'margin_amount') {
    return 'Clasificación por margen';
  }

  return 'Clasificación por margen %';
}


function metricLabel(
  metric:
    MarginCenterCustomerPositionRankingMetric,
): string {
  if (metric === 'revenue') {
    return 'Facturación';
  }

  if (metric === 'margin_amount') {
    return 'Margen';
  }

  return 'Margen %';
}


function formatMetricValue(
  metric:
    MarginCenterCustomerPositionRankingMetric,
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (
    metric === 'revenue'
    || metric === 'margin_amount'
  ) {
    return formatMarginCenterCurrency(
      value,
      currencyCode,
    );
  }

  return `${formatMarginCenterNumber(
    value,
    2,
  )} %`;
}


function formatSignedMetricValue(
  metric:
    MarginCenterCustomerPositionRankingMetric,
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (metric === 'margin_percentage') {
    const formatted =
      formatMarginCenterNumber(
        Math.abs(value),
        2,
      );

    if (value > 0) return `+${formatted} pp`;
    if (value < 0) return `−${formatted} pp`;

    return `${formatted} pp`;
  }

  const formatted =
    formatMarginCenterCurrency(
      Math.abs(value),
      currencyCode,
    );

  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;

  return formatted;
}


function movementColor(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
    || value === 0
  ) {
    return '#94a3b8';
  }

  return value > 0
    ? '#34d399'
    : '#fb7185';
}


function rankMovementText(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (value > 0) {
    return `↑ ${formatMarginCenterNumber(
      value,
      0,
    )}`;
  }

  if (value < 0) {
    return `↓ ${formatMarginCenterNumber(
      Math.abs(value),
      0,
    )}`;
  }

  return '=';
}


function rowBackground(
  row: MarginCenterCustomerPositionRankingRow,
): string {
  return row.is_target
    ? 'rgba(56,189,248,0.10)'
    : 'transparent';
}


export default function CustomerPositionRankingModal({
  open,
  customerId,
  metric,
  scope,
  groupDimension,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  currencyCode,
  onClose,
}: CustomerPositionRankingModalProps) {
  const params =
    useMemo<MarginCenterCustomerPositionRankingParams>(
      () => ({
        start_date:
          startDate ?? undefined,

        end_date:
          endDate ?? undefined,

        comparison_mode:
          comparisonMode,

        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,

        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,

        scope,

        group_dimension:
          scope === 'group'
            ? groupDimension
            : undefined,

        metric,

        limit: 25,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        groupDimension,
        metric,
        scope,
        startDate,
      ],
    );

  const rankingQuery = useQuery({
    queryKey: [
      'margin-center',
      'customer-position-ranking',
      customerId,
      params,
    ],
    queryFn: () =>
      getMarginCenterCustomerPositionRanking(
        customerId,
        params,
      ),
    enabled:
      open
      && Boolean(customerId)
      && (
        scope === 'global'
        || Boolean(groupDimension)
      ),
    retry: false,
  });

  const data = rankingQuery.data;
  const summary = data?.summary ?? null;
  const rows = data?.rows ?? [];

  const scopeTitle =
    data?.scope.dimension
      ? `${data.scope.dimension.label} · ${data.scope.dimension.value ?? '—'}`
      : 'Toda la cuenta';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: {
            xs: '95vw',
            sm: 980,
          },
          maxWidth: '95vw',
          height: {
            xs: '84vh',
            sm: 720,
          },
          maxHeight: '84vh',
          bgcolor: '#0b1220',
          border:
            '1px solid rgba(148,163,184,0.18)',
          borderRadius: 3,
          boxShadow:
            '0 24px 80px rgba(2,6,23,0.56)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          height: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          p: {
            xs: 2,
            md: 2.5,
          },
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            height: '100%',
            minHeight: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {metricTitle(metric)}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.25,
                  color: '#94a3b8',
                }}
              >
                {scopeTitle}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Cerrar"
              sx={{
                color: '#94a3b8',
                flexShrink: 0,
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          {rankingQuery.isFetching && (
            <LinearProgress />
          )}

          {rankingQuery.isError && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Alert
                severity="error"
                sx={{
                  width: '100%',
                  maxWidth: 620,
                }}
              >
                No se ha podido cargar esta clasificación.
              </Alert>
            </Box>
          )}

          {!rankingQuery.isError
            && !rankingQuery.isFetching
            && data
            && data.status !== 'ok' && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Alert
                severity="info"
                sx={{
                  width: '100%',
                  maxWidth: 640,
                }}
              >
                No hay una clasificación calculable para este criterio y periodo.
              </Alert>
            </Box>
          )}

          {!rankingQuery.isError
            && data?.status === 'ok'
            && summary && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.85,
                    borderRadius: 2,
                    border:
                      '1px solid rgba(148,163,184,0.12)',
                    bgcolor:
                      'rgba(15,23,42,0.26)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 750,
                    }}
                  >
                    Posición actual
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mt: 0.15,
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    #{formatMarginCenterNumber(
                      summary.current_rank,
                      0,
                    )} de {formatMarginCenterNumber(
                      summary.current_population,
                      0,
                    )}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    py: 0.85,
                    borderRadius: 2,
                    border:
                      '1px solid rgba(148,163,184,0.12)',
                    bgcolor:
                      'rgba(15,23,42,0.26)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 750,
                    }}
                  >
                    {metricLabel(metric)}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mt: 0.15,
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    {formatMetricValue(
                      metric,
                      summary.current_value,
                      currencyCode,
                    )}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1.25,
                    py: 0.85,
                    borderRadius: 2,
                    border:
                      '1px solid rgba(148,163,184,0.12)',
                    bgcolor:
                      'rgba(15,23,42,0.26)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 750,
                    }}
                  >
                    Cambio de posición
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mt: 0.15,
                      color:
                        movementColor(
                          summary.rank_change,
                        ),
                      fontWeight: 900,
                    }}
                  >
                    {rankMovementText(
                      summary.rank_change,
                    )}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                }}
              >
                Se muestran {data.page.returned_count} clientes alrededor de la posición actual.
              </Typography>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  border:
                    '1px solid rgba(148,163,184,0.14)',
                  borderRadius: 2.5,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 790,

                    '& .MuiTableCell-head': {
                      bgcolor: '#111827',
                      color: '#e2e8f0',
                      fontWeight: 850,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ width: 70 }}
                      >
                        #
                      </TableCell>

                      <TableCell>
                        Cliente
                      </TableCell>

                      <TableCell align="right">
                        Actual
                      </TableCell>

                      <TableCell align="right">
                        Comparación
                      </TableCell>

                      <TableCell align="right">
                        Cambio
                      </TableCell>

                      <TableCell align="right">
                        Posición comp.
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row) => (
                      <TableRow
                        key={row.customer_id}
                        sx={{
                          bgcolor:
                            rowBackground(row),

                          '&:hover': {
                            bgcolor:
                              row.is_target
                                ? 'rgba(56,189,248,0.14)'
                                : 'rgba(148,163,184,0.05)',
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                row.is_target
                                  ? '#38bdf8'
                                  : '#cbd5e1',
                              fontWeight:
                                row.is_target
                                  ? 900
                                  : 750,
                            }}
                          >
                            #{formatMarginCenterNumber(
                              row.rank,
                              0,
                            )}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.75}
                            useFlexGap
                            flexWrap="wrap"
                            alignItems="center"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#f8fafc',
                                fontWeight:
                                  row.is_target
                                    ? 900
                                    : 750,
                              }}
                            >
                              {row.label}
                            </Typography>

                            {row.is_target && (
                              <Chip
                                size="small"
                                label="Cliente actual"
                                sx={{
                                  height: 20,
                                  bgcolor:
                                    'rgba(56,189,248,0.12)',
                                  color: '#7dd3fc',
                                  border:
                                    '1px solid rgba(56,189,248,0.20)',
                                  fontSize:
                                    '0.65rem',
                                  fontWeight: 800,
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            color: '#e2e8f0',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatMetricValue(
                            metric,
                            row.current_value,
                            currencyCode,
                          )}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            color: '#94a3b8',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatMetricValue(
                            metric,
                            row.comparison_value,
                            currencyCode,
                          )}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                movementColor(
                                  row.change_value,
                                ),
                              fontWeight: 850,
                            }}
                          >
                            {formatSignedMetricValue(
                              metric,
                              row.change_value,
                              currencyCode,
                            )}
                          </Typography>

                          {row.change_pct !== null
                            && metric !==
                              'margin_percentage' && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: '#64748b',
                              }}
                            >
                              {row.change_pct > 0
                                ? '+'
                                : row.change_pct < 0
                                  ? '−'
                                  : ''}
                              {formatMarginCenterNumber(
                                Math.abs(
                                  row.change_pct,
                                ),
                                2,
                              )} %
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.comparison_rank !== null
                            ? (
                              <>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#cbd5e1',
                                    fontWeight: 750,
                                  }}
                                >
                                  #{formatMarginCenterNumber(
                                    row.comparison_rank,
                                    0,
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color:
                                      movementColor(
                                        row.rank_change,
                                      ),
                                  }}
                                >
                                  {rankMovementText(
                                    row.rank_change,
                                  )}
                                </Typography>
                              </>
                            )
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
